import { centsToFormattedDollars } from '@fruitful/fruitils';
import {
    type IncomeSplitExecution,
    type IncomeSplitOverviewHistory,
    selectedIncomeSplitExecutionAtom,
    selectedIncomeSplitExecutionIndexAtom,
    selectedIncomeSplitMonthAtom,
} from '@fruitful/income-split';
import { AnalyticsUserEvent } from '@fruitful/types';
import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import React from 'react';
import {
    ArrowRightIcon,
    BodyMediumRegular,
    BodySmallRegular,
    Icon,
    ScreenContainer,
    ScrollView,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { track } from '../../../../logger/Logger';
import { useGetIncomeSplitOverviewFromGlobalState } from '../hooks/useGetIncomeSplitOverviewFromGlobalState';

const formatMonthLabel = (monthKey: string) => {
    const [firstPart, secondPart] = monthKey.split('-');
    const firstNumber = Number(firstPart);
    const secondNumber = Number(secondPart);

    const year = firstPart?.length === 4 ? firstNumber : secondNumber;
    const month = firstPart?.length === 4 ? secondNumber : firstNumber;

    if (
        !Number.isInteger(year) ||
        !Number.isInteger(month) ||
        month < 1 ||
        month > 12
    ) {
        return monthKey;
    }

    return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        year: 'numeric',
    }).format(new Date(year, month - 1));
};

const IncomeSplitExecutionRow = ({
    execution,
    executionIndex,
    month,
}: {
    execution: IncomeSplitExecution;
    executionIndex: number;
    month: IncomeSplitOverviewHistory;
}) => {
    const setSelectedIncomeSplitExecution = useSetAtom(
        selectedIncomeSplitExecutionAtom,
    );
    const setSelectedIncomeSplitExecutionIndex = useSetAtom(
        selectedIncomeSplitExecutionIndexAtom,
    );
    const setSelectedIncomeSplitMonth = useSetAtom(
        selectedIncomeSplitMonthAtom,
    );

    return (
        <XStack
            alignItems="center"
            justifyContent="space-between"
            gap={12}
            cursor="pointer"
            onPress={() => {
                track(AnalyticsUserEvent.INCOME_SPLIT_DEPOSIT_DETAILS_CLICKED, {
                    executionId: execution.id,
                });
                setSelectedIncomeSplitMonth(month);
                setSelectedIncomeSplitExecution(execution);
                setSelectedIncomeSplitExecutionIndex(executionIndex);
                router.navigate({
                    pathname: '/income-split/overview',
                    params: { executionId: execution.id },
                });
            }}
        >
            <YStack gap={4} flexShrink={1}>
                <BodyMediumRegular numberOfLines={1}>
                    {execution.description}
                </BodyMediumRegular>
                <BodySmallRegular color="$gray1000">
                    {centsToFormattedDollars(execution.amount)} •{' '}
                    {execution.date}
                </BodySmallRegular>
            </YStack>
            <Icon icon={ArrowRightIcon} size={24} />
        </XStack>
    );
};

const IncomeSplitMonthGroup = ({
    month,
}: {
    month: IncomeSplitOverviewHistory;
}) => {
    const executions = month.executions.slice().reverse();

    if (executions.length === 0) {
        return null;
    }

    return (
        <YStack gap={12}>
            <BodyMediumRegular>
                {formatMonthLabel(month.month)}
            </BodyMediumRegular>
            <YStack backgroundColor="#FAFAFA" padding={16} gap={32}>
                {executions.map((execution, index) => (
                    <IncomeSplitExecutionRow
                        key={execution.id}
                        execution={execution}
                        executionIndex={month.executions.length - 1 - index}
                        month={month}
                    />
                ))}
            </YStack>
        </YStack>
    );
};

const IncomeSplitAllMonths = () => {
    const { data, isLoading, isError } =
        useGetIncomeSplitOverviewFromGlobalState();

    if (isLoading) {
        return (
            <ScreenContainer title="All Income Splits" hasBackButton>
                <YStack flex={1} alignItems="center" justifyContent="center">
                    <Spinner />
                </YStack>
            </ScreenContainer>
        );
    }

    if (isError || !data) {
        return (
            <ScreenContainer title="All Income Splits" hasBackButton>
                <YStack flex={1} alignItems="center" justifyContent="center">
                    <BodyMediumRegular>
                        Unable to load Income Split data. Please try again
                        later.
                    </BodyMediumRegular>
                </YStack>
            </ScreenContainer>
        );
    }

    const monthsWithExecutions = data.history
        .filter((month) => month.executions.length > 0)
        .reverse();

    if (monthsWithExecutions.length === 0) {
        return (
            <ScreenContainer title="All Income Splits" hasBackButton>
                <YStack
                    gap={16}
                    alignItems="center"
                    justifyContent="center"
                    flex={1}
                    paddingHorizontal={24}
                >
                    <BodyMediumRegular textAlign="center">
                        No Income Splits...yet
                    </BodyMediumRegular>
                    <BodySmallRegular textAlign="center" color="$gray1000">
                        When a qualifying deposit arrives, you&apos;ll be able
                        to see the details of your split here
                    </BodySmallRegular>
                </YStack>
            </ScreenContainer>
        );
    }

    return (
        <ScreenContainer title="All Income Splits" hasBackButton>
            <ScrollView showsVerticalScrollIndicator={false}>
                <YStack gap={24} paddingBottom={24}>
                    {monthsWithExecutions.map((month) => (
                        <IncomeSplitMonthGroup
                            key={month.month}
                            month={month}
                        />
                    ))}
                </YStack>
            </ScrollView>
        </ScreenContainer>
    );
};

export default IncomeSplitAllMonths;
